const ALWAYS_GROUP = ['it']
const GROUP_FROM_FIVE_DIGITS = ['uk']
const INTL_LOCALES = { pt: 'pt-PT' }

export default {
  slugify: (str, maxLength) => {
    const split = str.substr(0, maxLength)
    return split
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[^a-z0-9\s-]/g, '') // Remove non-alphanumeric characters except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Remove consecutive hyphens
  },

  isBase64StringSVG (base64String) {
    // Decode the base64 string to binary data
    const decodedData = atob(base64String)

    // Check if the decoded data starts with an SVG XML declaration and contains an <svg> element
    return decodedData.startsWith('<?xml version=') && decodedData.includes('<svg')
  },

  debounce: (callback, wait, immediate = false) => {
    let timeout = null

    return function () {
      const callNow = immediate && !timeout
      const next = () => callback.apply(this, arguments)

      clearTimeout(timeout)
      timeout = setTimeout(next, wait)
      if (callNow) {
        next()
      }
    }
  },

  getRemainingTrialDays: (validTo, now = new Date()) => {
    if (!validTo) {
      return 0
    }

    const validToDate = new Date(validTo)
    const currentDate = now instanceof Date ? now : new Date(now)

    if (Number.isNaN(validToDate.getTime()) || Number.isNaN(currentDate.getTime())) {
      return 0
    }

    const millisecondsInDay = 1000 * 60 * 60 * 24
    const remainingDays = Math.ceil((validToDate.getTime() - currentDate.getTime()) / millisecondsInDay)

    return Math.max(remainingDays, 0)
  },

  /**
   * @param {number} value
   * @param {string} locale  BCP 47 tag, e.g. this.$i18n.locale
   * @param {Intl.NumberFormatOptions} [options]  e.g. { style: 'currency', currency: 'USD' }
   * @returns {string}
   */
  formatNumber: (value, locale, options = {}) => {
    const tag = INTL_LOCALES[locale] || locale
    if (GROUP_FROM_FIVE_DIGITS.includes(locale) && Math.abs(value) < 10000) {
      return new Intl.NumberFormat(tag, { ...options, useGrouping: false }).format(value)
    }
    const formatter = new Intl.NumberFormat(tag, options)
    if (!ALWAYS_GROUP.includes(locale)) {
      return formatter.format(value)
    }
    const parts = formatter.formatToParts(value)
    if (Math.abs(value) < 1000 || parts.some((part) => part.type === 'group')) {
      return formatter.format(value)
    }
    const group = formatter.formatToParts(1000000).find((part) => part.type === 'group')
    const separator = group ? group.value : ','
    return parts
      .map((part) => part.type === 'integer' ? part.value.replace(/\B(?=(\d{3})+(?!\d))/g, separator) : part.value)
      .join('')
  },

  formatDate: (string, short) => {
    let date
    if (!string) {
      date = new Date()
    } else {
      date = new Date(string)
    }

    const minutes = date.getMinutes() <= 9 ? '0' + date.getMinutes() : date.getMinutes()
    const days = date.getDate() <= 9 ? '0' + date.getDate() : date.getDate()
    const months = (date.getMonth() + 1) <= 9 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1

    if (short) {
      return `${date.getFullYear()}-${months}-${days}`
    } else {
      return `${date.getFullYear()}-${months}-${days} ${date.getHours()}:${minutes}`
    }
  },

  deepCompare: function (a, b) {
    if (a === b) return true
    if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime()
    if (!a || !b || (typeof a !== 'object' && typeof b !== 'object')) return a === b
    if (a === null || a === undefined || b === null || b === undefined) return false
    if (a.prototype !== b.prototype) return false
    const keys = Object.keys(a)
    if (keys.length !== Object.keys(b).length) return false
    return keys.every((k) => {
      if (a[k] !== undefined && b[k] !== undefined) {
        return this.deepCompare(JSON.parse(JSON.stringify(a[k])), JSON.parse(JSON.stringify(b[k])))
      } else {
        return false
      }
    })
  },

  isObject: function (item) {
    return (item && typeof item === 'object' && !Array.isArray(item))
  },

  mergeDeep: function (target, ...sources) {
    if (!sources.length) return target
    const source = sources.shift()

    if (this.isObject(target) && this.isObject(source)) {
      for (const key in source) {
        if (this.isObject(source[key])) {
          if (!target[key]) Object.assign(target, { [key]: {} })
          this.mergeDeep(target[key], source[key])
        } else {
          if ((source[key] && source[key].length) || typeof source[key] === 'boolean' || typeof source[key] === 'number') {
            Object.assign(target, { [key]: source[key] })
          } else {
            Object.assign(target, { [key]: target[key] })
          }
        }
      }
    }

    return this.mergeDeep(target, ...sources)
  },

  CODE_TYPE: {
    GS1: 'gs1',
    DEFAULT: 'default'
  },

  isAlphaNumeric: (value) => {
    return /^[a-zA-Z0-9]+$/.test(value)
  },

  isNumeric: (value) => {
    return /^[0-9]+$/.test(value)
  },

  generateLotNumber: () => {
    const regexPattern = /^[a-zA-Z0-9]+$/
    let lotNumber = ''

    while (lotNumber.length < 6) {
      const randChar = String.fromCharCode(Math.floor(Math.random() * 123))
      if (regexPattern.test(randChar)) lotNumber += randChar
    }

    return lotNumber
  },

  AI: {
    GTIN: '01',
    LOT_BATCH_NUMBER: '10',
    CPV: '22',
    SERIAL_NUMBER: '21'
  },

  isGS1Identifier: (message) => {
    const regex = /\//

    return message && regex.test(message)
  },

  excludedTagRegEx: /^(scantrust_.*|cpv|lot|gtin|gs1_.*)$/,

  /*
   * Normalize backend validation errors into a { field: [messages] } map
   * usable directly with Vuetify's :error-messages.
   * Handles two formats:
   *  - DRF style:   { "name": ["msg"], "city": ["msg"] }
   *  - Paddle style: { error_code, error_message, field_errors: [{ field, error }] }
   */
  parseFieldErrors: (data) => {
    if (!data || typeof data !== 'object') {
      return {}
    }

    if (Array.isArray(data.field_errors)) {
      return data.field_errors.reduce((errors, { field, error }) => {
        if (!field) {
          return errors
        }
        if (!errors[field]) {
          errors[field] = []
        }
        errors[field].push(error)
        return errors
      }, {})
    }

    return data
  }

}
